import React from 'react';
import PropTypes from 'prop-types';
import { IconButton, MenuItem, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useProfessionals } from '../../../hooks/useProfessionals';
import {
  groupByLabels,
  groupByOptions,
  productionStatus,
  productionStatusLabels,
} from '../hooks/useClinicalProductionScreen';

export default function ClinicalProductionFilters({
  selectedDate,
  onChangeDate,
  selectedStatus,
  onChangeStatus,
  selectedProfessionalId,
  onChangeProfessionalId,
  groupBy,
  onChangeGroupBy,
  isMobile,
}) {
  const { professionals } = useProfessionals();

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1.5}
      sx={{ mb: 2, flexWrap: 'wrap' }}
      useFlexGap
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 210 }}>
        <IconButton
          size="small"
          aria-label="Mês anterior"
          onClick={() => onChangeDate(selectedDate.subtract(1, 'month'))}
        >
          <ChevronLeft size={18} />
        </IconButton>

        <DatePicker
          label="Período"
          views={['month', 'year']}
          value={selectedDate}
          onChange={(date) => date && onChangeDate(date)}
          format="MM/YYYY"
          slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
        />

        <IconButton
          size="small"
          aria-label="Próximo mês"
          onClick={() => onChangeDate(selectedDate.add(1, 'month'))}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Stack>

      <TextField
        select
        label="Situação"
        value={selectedStatus}
        onChange={(event) => onChangeStatus(event.target.value)}
        fullWidth
        sx={{ flex: 1, minWidth: 170 }}
      >
        {Object.values(productionStatus).map((status) => (
          <MenuItem key={status} value={status}>
            {productionStatusLabels[status]}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Profissional"
        value={selectedProfessionalId}
        onChange={(event) => onChangeProfessionalId(event.target.value)}
        fullWidth
        sx={{ flex: 1, minWidth: 170 }}
        slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
      >
        <MenuItem value="">Todos</MenuItem>
        {professionals.map((professional) => (
          <MenuItem key={professional.id} value={professional.id}>
            {professional.nome || professional.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Agrupamento"
        value={groupBy}
        onChange={(event) => onChangeGroupBy(event.target.value)}
        fullWidth
        sx={{ flex: 1, minWidth: 170 }}
      >
        {Object.values(groupByOptions).map((option) => (
          <MenuItem key={option} value={option}>
            {groupByLabels[option]}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

ClinicalProductionFilters.propTypes = {
  selectedDate: PropTypes.object.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  selectedStatus: PropTypes.string.isRequired,
  onChangeStatus: PropTypes.func.isRequired,
  selectedProfessionalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangeProfessionalId: PropTypes.func.isRequired,
  groupBy: PropTypes.string.isRequired,
  onChangeGroupBy: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

ClinicalProductionFilters.defaultProps = {
  isMobile: false,
  selectedProfessionalId: '',
};
